import { i18n, type SupportedLanguage } from "@/services/locale";
import { useAiNotificationStore } from "@/stores/ai-notification";
import { logger } from "@/utils/debug";
import type { AiIntentGuess } from "@/types/ai";
import type { AiAssistant } from "@/types/ai/Assistant";
import type { AiContext } from "@/types/ai/Context";
import { Assistant } from "./Assistant";

/**
 * Intent Assistant - Determines user intent based on URL context and prompt
 */
export class IntentAssistant extends Assistant {
  private context: AiContext;
  private language: SupportedLanguage;

  constructor(context: AiContext) {
    super();
    this.context = context;
    this.language = i18n.getCurrentLanguage();
  }

  config(): AiAssistant {
    return {
      systemPrompt: this.getSystemPrompt(),
      model: "gpt-4o",
      temperature: 0.1,
      maxTokens: 200,
      language: this.language,
      responseFormat: "json_object",
    };
  }

  private getIntentsSchema(): Record<string, any> {
    return {
      discover: {
        description: "Find/explore datasets",
        examples: ["find datasets about", "search for", "browse"],
        context: "Anywhere",
      },
      build: {
        description: "Manage projects/workspaces",
        examples: [
          "create project",
          "delete workspace",
          "list my projects",
          "open project",
        ],
        context: "Must be on build overview (not inside workspace)",
      },
      workspace: {
        description:
          "Manipulate workspace (filters, map, transformers, groups)",
        examples: [
          "add filter",
          "show on map",
          "group by",
          "add transformer",
          "hide groups",
          "show my place",
        ],
        context: "Must be inside workspace",
      },
      app: {
        description: "Help, navigation, or app settings",
        examples: [
          "how do I",
          "what can you do",
          "help",
          "settings",
          "navigate to",
        ],
        context: "Anywhere",
      },
      reply: {
        description: "Simple answer, no data/actions needed",
        examples: ["what is", "explain", "tell me about"],
        context: "Anywhere",
      },
    };
  }

  getSystemPrompt(): string {
    const pathname = this.context.pathname;

    const intentsSchema = this.getIntentsSchema();

    return `
      You are an intent classifier. 
      Return ONLY valid JSON: {
        "reply": "short user-friendly thinking process explanation", 
        "data": { "intent": "discover|build|workspace|app|reply", "confidence": 0.0-1.0 }
      }
      PATHNAME: ${pathname}

      PATHNAME-BASED RULES (primary):
      1. If pathname = /build/:projectId/:dataflowId → intent = "workspace" (unless user explicitly asks to navigate away)
      2. If pathname = /build or /build/:projectId (no dataflowId) → intent = "build"
      3. If pathname starts with /discover → intent = "discover" (unless user explicitly asks for something else)

      PROMPT-BASED RULES (secondary):
      - "find/search/browse datasets" → "discover"
      - "create/delete/list project/workspace" → "build"  
      - "add filter/show on map/group by/transform" → "workspace"
      - "how do I/help/settings/navigate" → "app"
      - "what is/explain" → "reply"

      INTENTS:
      ${JSON.stringify(intentsSchema, null, 2)}

      REPLY GUIDELINES:
      - The "reply" field is shown to the user as you execute actions
      - Make it user-friendly and contextual to what you're doing
      - Use present tense, conversational tone
      - Examples:
        * "Show me Spain" → reply: "Let me find Spain on the map"
        * "Filter by city" → reply: "Adding a filter for city"
        * "Group by country" → reply: "Grouping data by country"
        * "Show Denmark" → reply: "Navigating to Denmark on the map"
      
    `;
  }

  async execute(prompt: string): Promise<AiIntentGuess | undefined> {
    const { showNotification } = useAiNotificationStore.getState();

    const response = await Assistant.sendPrompt<AiIntentGuess>(
      prompt,
      this.config()
    );

    if (response.error) {
      showNotification(response.error, true);
      return undefined;
    }

    logger.log("[IntentAssistant] Full response:", response);

    if (response.reply) {
      showNotification(response.reply, false);
    }

    if (response.data) {
      const { intent, confidence } = response.data;
      logger.log("[IntentAssistant] Intent guess:", { intent, confidence });
      
      if (intent && confidence !== undefined) {
        return {
          intent,
          confidence,
          prompt: prompt,
        };
      } else {
        logger.warn("[IntentAssistant] Missing intent or confidence in data:", response.data);
      }
    } else {
      logger.warn("[IntentAssistant] No data field in response:", response);
    }

    return undefined;
  }
}
